import { resolveEffectiveRoomGeometry, resolveEffectiveProjectGeometry, resolveUnitQuantity } from '@/shared/lib/geometry';
import { parseSafeNumber, roundTo } from '@/shared/lib/number';
import {
  DEFAULT_RECIPE_COEFFICIENT,
  PERCENT_DIVISOR,
} from '@/shared/lib/geometry-constants';
import { applyTypedAdjustment } from './computeTotals';
import type { Room } from '@/entities/room/model/types';
import type { Recipe, RecipeLayer } from '@/entities/recipe/model/types';
import type { Service } from '@/entities/service/model/types';
import type { CalculatedEstimateItem, RoomCalculationResult, EstimateItem } from '../model/types';

export type BaseRoom = {
  dims: Room['dims'];
  openings?: Room['openings'];
  partitions?: Room['partitions'];
  wallElements?: Room['wallElements'];
  items: Array<Partial<Room['items'][number]> & { id: string; unitType?: string; priceLabor: number }>;
};

/**
 * Расчет итогов по одной комнате
 */
export const calculateRoomTotals = (room: Room | BaseRoom, safetyMargin: number = 0): RoomCalculationResult => {
  if (!room) return { total: 0, items: [] };

  const roomGeom = resolveEffectiveRoomGeometry(room);

  const marginFactor = 1 + (safetyMargin / PERCENT_DIVISOR);

  let total = 0;
  const calculatedItems = ((room as Room).items || []).map(item => {
    const unitType = item.unitType || 'pcs';
    const userQty = parseSafeNumber(item.userQty, 0);
    
    const baseQty = resolveUnitQuantity(unitType, roomGeom, userQty); 
    let workingQty = userQty > 0 ? userQty : baseQty;

    workingQty = applyTypedAdjustment(workingQty, item.adjustment);

    const finalQty = Math.max(0, workingQty * marginFactor);
    const price = parseSafeNumber(item.priceLabor, 0);
    const cost = roundTo(finalQty * price, 2);
    total += cost;

    return {
        ...item,
        qty: finalQty,
        baseQty: baseQty,
        cost
    };
  });

  return {
      total: roundTo(total, 2),
      items: calculatedItems as CalculatedEstimateItem[]
  };
};

/** Alias for compatibility */
export const calculateRoomTotal = calculateRoomTotals;

/**
 * Расчет общей сметы проекта
 */
export const calculateProjectTotals = (rooms: Room[], safetyMargin: number = 0): number => {
    return rooms.reduce((acc, room) => acc + calculateRoomTotals(room, safetyMargin).total, 0);
};

/** Alias for compatibility */
export const calculateProjectTotal = calculateProjectTotals;

/**
 * Расчет стоимости одной единицы пакета услуг (рецепта).
 */
export const calculateRecipeUnitCost = (recipe: Recipe, servicesDB: Service[]): number => {
  if (!recipe || !recipe.layers) return 0;
  
  return recipe.layers.reduce((acc: number, layer: RecipeLayer) => {
    const service = servicesDB.find(s => s.id === layer.serviceId);
    if (!service) return acc;
    
    const coeff = typeof layer.coefficient === 'number' ? layer.coefficient : DEFAULT_RECIPE_COEFFICIENT;
    return acc + (service.priceLabor * coeff);
  }, 0);
};

/**
 * Расчет глобальных (project-level) позиций.
 * Поддерживает полноценную "проектную геометрию" через projectOverrides (manual overrides на проект).
 * Для unitTypes без геом (pcs и т.п.) — строго userQty.
 */
export const calculateGlobalItemsTotals = (
  items: EstimateItem[] = [], 
  safetyMargin: number = 0,
  projectOverrides: { floorArea?: number; wallArea?: number; perimeter?: number } = {}
): RoomCalculationResult => {
  if (!items || items.length === 0) return { total: 0, items: [] };

  // Проектная "геометрия" для resolve (0 если не задана — fallback на userQty)
  const projectGeom = {
    floorArea: projectOverrides.floorArea ?? 0,
    wallArea: projectOverrides.wallArea ?? 0,
    perimeter: projectOverrides.perimeter ?? 0,
    height: 0,
    openingsArea: 0,
    partitionsArea: 0,
    windowSlopes: 0,
    doorSlopes: 0,
    partitionsEndsLinear: 0,
    nichesArea: 0,
    ledgesArea: 0,
  };

  const marginFactor = 1 + (safetyMargin / PERCENT_DIVISOR);
  let total = 0;

  const calculatedItems = items.map(item => {
    const unitType = item.unitType || 'pcs';
    const userQty = parseSafeNumber(item.userQty, 0);

    // Для глобальных: если есть overrides и unitType требует геом — используем projectGeom, иначе base=0
    const baseQty = resolveUnitQuantity(unitType, projectGeom, userQty);
    let workingQty = userQty > 0 ? userQty : (projectOverrides.floorArea || projectOverrides.wallArea || projectOverrides.perimeter ? baseQty : 0);

    workingQty = applyTypedAdjustment(workingQty, item.adjustment);

    const finalQty = Math.max(0, workingQty * marginFactor);
    const price = parseSafeNumber(item.priceLabor, 0);
    const cost = roundTo(finalQty * price, 2);
    total += cost;

    return {
      ...item,
      qty: finalQty,
      baseQty,
      cost,
    } as CalculatedEstimateItem;
  });

  return {
    total: roundTo(total, 2),
    items: calculatedItems,
  };
};

/**
 * Полный расчёт проекта (глобальный слой + комнаты).
 * Возвращает общий тотал (для обратной совместимости старый calculateProjectTotals оставлен).
 */
export const calculateFullProjectTotal = (
  rooms: Room[] = [],
  projectItems: EstimateItem[] = [],
  safetyMargin: number = 0,
  projectOverrides: { floorArea?: number; wallArea?: number; perimeter?: number } = {}
): number => {
  const roomsTotal = calculateProjectTotals(rooms, safetyMargin);
  const resolvedProjectGeometry = resolveEffectiveProjectGeometry(rooms, projectOverrides);
  const globalRes = calculateGlobalItemsTotals(projectItems, safetyMargin, resolvedProjectGeometry);
  return roundTo(roomsTotal + globalRes.total, 2);
};